import React, { useState, useEffect, useMemo } from "react";
import ApiCallService from "../../../Service/ApiCallService";
import {
  Paper,
  Box,
  Chip,
  Select,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip as MuiTooltip,
  useMediaQuery,
} from "@mui/material";
import ReactTable from "../../../components/common/ReactTable";
import CustomSelect from "../../../components/common/CustomSelect";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import { Doughnut } from "react-chartjs-2";
import CountUpAnimation from "../../../components/common/CountUpAnimation";
import { useTheme } from "@mui/material/styles";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { usePermission } from "../../../permissions/usePermission";
import * as XLSX from "xlsx";
import {downloadChapterExcel} from "../../../utils/chapterExcelExport.js"
ChartJS.register(ArcElement, Tooltip, Legend);

const ChapterPage = () => {

  const environment = process.env.REACT_APP_ENV !== "production"
  const [loading, setLoading] = useState(false);
  const [chapterData, setChapterData] = useState([]);
  const [districts, setDistricts] = useState(null);
  const [regions, setRegions] = useState([]);
  const [stateID, setStateID] = useState([]);
  const [filterCategory, setFilterCategory] = useState("All");
  const [selectedState, setSelectedState] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [openModal, setOpenModal] = useState(null);
  const [isLeader, setIsLeader] = useState(false);
  const [isLandingPage, setIsLandingPage] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [reactTableAs, setReactTableAs] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isSmallScreen = useMediaQuery("(max-width:768px)");

  useEffect(() => {
		window.scrollTo({top:0, behavior:"smooth"})
	},[])

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      category: "All",
      state: "All",
      region: "All",
      district: "All",
    },
  });
  const permissionsState = useSelector((state) => state.permissions);
  const userDetails = useSelector((state) => state.user?.userDetails);
  const scopes = Array.isArray(permissionsState?.permissions?.scopes)
    ? permissionsState.permissions.scopes
    : [];
  const { canByKey } = usePermission();
  const canCreateChapter = canByKey("CHAPTER_MANAGEMENT_CHAPTERS_CREATE");
  const canViewChapter = canByKey("CHAPTER_MANAGEMENT_CHAPTERS_VIEW");

  const normalizeScope = (scope) => {
    const payload = scope?.scope || scope;
    return {
      level: payload?.level,
      state: payload?.state,
      district: payload?.district,
      region: payload?.region,
      taluka: payload?.taluka,
    };
  };

  const chapterScopes = useMemo(
    () => scopes.filter((scope) => ["CHAPTER", "COMMITTEE"].includes(scope?.sourceType)),
    [scopes],
  );

  const allowedStateIds = useMemo(() => {
    if (!chapterScopes.length) return [];
    const ids = chapterScopes
      .map((scope) => normalizeScope(scope).state)
      .filter(Boolean);
    return [...new Set(ids)];
  }, [chapterScopes]);

  const allowedDistrictIds = useMemo(() => {
    if (!chapterScopes.length) return [];
    const ids = chapterScopes
      .map((scope) => normalizeScope(scope).district)
      .filter(Boolean);
    return [...new Set(ids)];
  }, [chapterScopes]);

  const hasScopeRestriction = chapterScopes.length > 0;

  const extractId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value.$oid) return value.$oid;
    if (value._id) return extractId(value._id);
    return "";
  };

  const resolveLocationName = (value, list, labelKey) => {
    if (!value) return "-";
    if (typeof value === "object") {
      return value?.[labelKey] || value?.name || "-";
    }
    const found = (Array.isArray(list) ? list : []).find(
      (item) => String(item?._id) === String(value),
    );
    return found?.[labelKey] || "-";
  };
  const chapterStateIds = useMemo(() => {
    const ids = new Set();
    (chapterData || []).forEach((chapter) => {
      const id = extractId(chapter?.location?.state);
      if (id) ids.add(id);
    });
    return ids;
  }, [chapterData]);

  const chapterDistrictIdsByState = useMemo(() => {
    const map = new Map();
    (chapterData || []).forEach((chapter) => {
      const stateId = extractId(chapter?.location?.state);
      const districtId = extractId(chapter?.location?.district);
      if (!stateId || !districtId) return;
      if (!map.has(stateId)) map.set(stateId, new Set());
      map.get(stateId).add(districtId);
    });
    return map;
  }, [chapterData]);

  const category = useWatch({ control, name: "category" });
  const stateId = useWatch({ control, name: "state" });
  const regionId = useWatch({ control, name: "region" });
  const districtId = useWatch({ control, name: "district" });

  useEffect(() => {
    setReactTableAs(tabValue === 0); // 0 = Chapters (true), 1 = Requests (false)
  }, [tabValue]);

  useEffect(() => {
    if (location.pathname == "/superuser/chapters") {
      setIsLeader(true);
    } else if (location.pathname == "/chapter") {
      setIsLandingPage(true);
    } else {
      setIsLeader(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    setLoading(true);
    const fetchChapters = async () => {
      try {
        const res = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/getAllChapters`,
        );
        const latestUpdatedAt = Array.isArray(res.data)
          ? res.data.reduce((acc, item) => {
            const ts = item?.updatedAt;
            if (!ts) return acc;
            const date = new Date(ts);
            return !acc || date > acc ? date : acc;
          }, null)
          : null;

        setChapterData(res.data);
        setLastUpdated(latestUpdatedAt);
      } catch (err) {
        console.error("Error Found : ", err);
      }
    };

    const fetchStates = async () => {
      try {
        const res = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/states`,
        );
        setStateID(res || []);
      } catch (err) {
        console.error("Error Found : ", err);
      }
    };

    Promise.all([fetchChapters(), fetchStates()]).finally(() =>
      setLoading(false),
    );
  }, []);

  useEffect(() => {
    if (!stateId || stateId === "All") {
      setRegions([]);
      setDistricts([]);
      return;
    }

    const fetchRegionsAndDistricts = async () => {
      try {
        setLoading(true);

        const [regionsRes, districtsRes] = await Promise.all([
          ApiCallService.get(
            `${process.env.REACT_APP_BASE_URL}/state/${stateId}/region`,
          ),
          ApiCallService.get(
            `${process.env.REACT_APP_BASE_URL}/state/${stateId}/district`,
          ),
        ]);
        setRegions(regionsRes?.data || []);
        setDistricts(districtsRes?.data || []);
      } catch (err) {
        console.error("Error fetching regions/districts:", err);
        setRegions([]);
        setDistricts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRegionsAndDistricts();
  }, [stateId]);

  useEffect(() => {
    if (!hasScopeRestriction) return;

    if (allowedStateIds.length) {
      const preferredState = allowedStateIds[0];
      const isValidState =
        stateId === "All" || allowedStateIds.includes(stateId);

      if (
        !isValidState ||
        (allowedStateIds.length === 1 && stateId !== preferredState)
      ) {
        setValue("state", preferredState, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }
  }, [allowedStateIds, hasScopeRestriction, setValue, stateId]);

  useEffect(() => {
    if (!hasScopeRestriction || !allowedDistrictIds.length) return;

    const preferredDistrict = allowedDistrictIds[0];
    const isValidDistrict =
      districtId === "All" || allowedDistrictIds.includes(districtId);

    if (
      !isValidDistrict ||
      (allowedDistrictIds.length === 1 && districtId !== preferredDistrict)
    ) {
      setValue("district", preferredDistrict, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [allowedDistrictIds, hasScopeRestriction, setValue, districtId]);

  const filteredStates = useMemo(() => {
    if (!stateID) return [];

    let list = (stateID || []).filter((state) =>
      chapterStateIds.has(extractId(state?._id)),
    );

    if (hasScopeRestriction && allowedStateIds.length) {
      list = list.filter((state) =>
        allowedStateIds.includes(extractId(state?._id)),
      );
    }

    return list;
  }, [stateID, chapterStateIds, hasScopeRestriction, allowedStateIds]);

  const filteredRegions = useMemo(() => {
    if (!stateId || stateId === "All") return [];

    const regionIds = new Set(
      (chapterData || [])
        .filter((chapter) => extractId(chapter?.location?.state) === String(stateId))
        .map((chapter) => extractId(chapter?.location?.region))
        .filter(Boolean),
    );

    return (regions || []).filter((region) =>
      regionIds.has(extractId(region?._id)),
    );
  }, [chapterData, regions, stateId]);

  const filteredDistricts = useMemo(() => {
    if (!stateId || stateId === "All") return [];

    let scopedChapters = (chapterData || []).filter(
      (chapter) => extractId(chapter?.location?.state) === String(stateId),
    );

    if (regionId && regionId !== "All") {
      scopedChapters = scopedChapters.filter(
        (chapter) => extractId(chapter?.location?.region) === String(regionId),
      );
    }

    const districtIds = new Set(
      scopedChapters
        .map((chapter) => extractId(chapter?.location?.district))
        .filter(Boolean),
    );

    let filtered = (districts || []).filter((district) =>
      districtIds.has(extractId(district?._id)),
    );

    if (hasScopeRestriction && allowedDistrictIds.length) {
      filtered = filtered.filter((district) =>
        allowedDistrictIds.includes(extractId(district?._id)),
      );
    }

    return filtered;
  }, [
    chapterData,
    districts,
    stateId,
    regionId,
    hasScopeRestriction,
    allowedDistrictIds,
  ]);

  const shouldShowAllStateOption =
    !hasScopeRestriction || allowedStateIds.length > 1;
  const shouldShowAllRegionOption = filteredRegions.length !== 1;
  const shouldShowAllDistrictOption = filteredDistricts.length !== 1;

  useEffect(() => {
    if (!filteredStates?.length) return;
    if (!stateId || stateId === "All") return;

    const isValidState = filteredStates.some(
      (state) => extractId(state?._id) === String(stateId),
    );

    if (!isValidState) {
      setValue("state", "All", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      setValue("region", "All", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      setValue("district", "All", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [filteredStates, stateId, setValue]);

  useEffect(() => {
    setValue("region", "All", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [stateId, setValue]);

  useEffect(() => {
    if (!stateId || stateId === "All") return;
    if (!regionId || regionId === "All") return;

    const isValidRegion = (filteredRegions || []).some(
      (region) => extractId(region?._id) === String(regionId),
    );

    if (!isValidRegion) {
      setValue("region", "All", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [filteredRegions, stateId, regionId, setValue]);

  useEffect(() => {
    if (!stateId || stateId === "All") return;

    setValue("district", "All", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [regionId, stateId, setValue]);

  useEffect(() => {
    if (!stateId || stateId === "All") return;
    if (!districtId || districtId === "All") return;

    const isValidDistrict = (filteredDistricts || []).some(
      (district) => extractId(district?._id) === String(districtId),
    );

    if (!isValidDistrict) {
      setValue("district", "All", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [filteredDistricts, stateId, districtId, setValue]);

  const handleFilterCategoryChange = (e) => setFilterCategory(e.target.value);
  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setDistricts(null);
  };

  const handleDistrictChange = (e) => {
    setDistricts(e.target.value);
  };

  const statCard = (accent) => ({
    p: 1.5,
    borderRadius: 2,
    bgcolor: "#F2F9FF",
    backgroundColor: "#F2F9FF",
    backgroundImage: "none",
    opacity: 1,
    width: "100%",
    height: 96,
    minHeight: 84,
    border: "1px solid " + accent,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 0.5,
    position: "relative",
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
    cursor: "default",
    transition: "transform 180ms ease, box-shadow 180ms ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 18px rgba(0,0,0,0.12)",
    },
  });

  const handleOpenModal = (chapter) => {
    setSelectedChapter(chapter);
    setOpenModal(true);

    let noPrograms;
    let programEntries;

    programEntries = Object.entries(chapter)
      .filter(
        ([key]) =>
          ![
            "_id",
            "stateShortCode",
            "talukaName",
            "chapterName",
            "districtName",
            "bjsState",
            "presidentName",
            "secretaryName",
            "VibrantAspirationalStatus",
          ].includes(key),
      )
      .map(([key, value]) => ({ name: key, status: value }));
    noPrograms = programEntries.filter((program) => program.status === "No");
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const sanitizeCsvValue = (value) => {
    const text = String(value ?? "").replace(/"/g, '""');
    return `"${text}"`;
  };

  const columns = [
    {
      field: "chapterName",
      headerName: "Chapter",
    },
    {
      field: "districtName",
      headerName: "District",
      disableSort: true,
      disableSearch: true,
    },
    {
      field: "regionName",
      headerName: "Region",
      disableSort: true,
      disableSearch: true,
    },
    {
      field: "stateName",
      headerName: "State",
      disableSort: true,
      disableSearch: true,
    },
    {
      field: "members",
      headerName: "Head Count",
      disableSort: true,
      disableSearch: true,
    },
    ...(!reactTableAs
      ? [
        {
          field: "chapterStatus",
          headerName: "Chapter Status",
          disableSort: true,
          disableSearch: false,
        },
      ]
      : []),
    {
      field: "chapterLifecycleStatus",
      headerName: "Status",
      disableSort: true,
      disableSearch: true,
      renderCell: (row) => (
        <Chip
          label={row.chapterLifecycleStatus}
          size="small"
          sx={{
            bgcolor:
              row.chapterLifecycleStatus === "Prospective"
                ? "#FF8C6D"
                : "#69BBFF",
            color: "#ffffff",
            fontWeight: 600,
          }}
        />
      ),
    },
    // ...(canViewChapter
    //   ? [
    {
      field: "action",
      headerName: "Actions",
      disableSort: true,
      disableSearch: true,
      renderCell: (row) => (
        <Button
          variant="contained"
          onClick={() => {
            navigate(`/chapterDetailsPage/${row._id}`, {
              state: { from: "View" },
            });
          }}
          sx={{
            backgroundColor: "#F2F9FF",
            color: "#202020",
            "&:hover": { transform: "translateY(-3px)", backgroundColor: "#F2F9FF", color: "#202020" },
          }}
        >
          View
        </Button>
      ),
    },
    //   ]
    // : []),
  ];

  const districtOptions = useMemo(() => {
    if (!stateId || stateId === "All") return [];

    return [
      ...new Set(
        chapterData
          .filter((d) => d?.location?.state?._id === stateId)
          .map((d) => d?.location?.district?.districtName),
      ),
    ];
  }, [chapterData, stateId]);

  const baseRows = useMemo(() => {
    const restricted = hasScopeRestriction;
    return (chapterData || [])

      .filter((f) => {
        if (!restricted) return true;

        const chapterStateId = f?.location?.state?._id;
        const chapterDistrictId = f?.location?.district?._id;

        if (
          allowedStateIds.length &&
          !allowedStateIds.includes(chapterStateId)
        ) {
          return false;
        }

        if (
          allowedDistrictIds.length &&
          !allowedDistrictIds.includes(chapterDistrictId)
        ) {
          return false;
        }

        return true;
      })

      .filter((f) =>
        category === "All" ? true : f.chapterLifecycleStatus === category,
      )

      .filter((f) =>
        stateId && stateId !== "All"
          ? f?.location?.state?._id === stateId
          : true,
      )

      .filter((f) =>
        regionId && regionId !== "All"
          ? f?.location?.region?._id === regionId
          : true,
      )

      .filter((f) =>
        districtId && districtId !== "All"
          ? f?.location?.district?._id === districtId
          : true,
      )

      .filter((f) =>
        reactTableAs
          ? f.chapterStatus === "Approved"
          : ["Rejected", "Pending"].includes(f.chapterStatus)
      )

      .map((d) => ({
        chapterName: d.chapterName,
        stateName: d?.location?.state?.stateName || "-",
        regionName:
          !d?.location?.region?.regionName ||
            d?.location?.region?.regionName === "N/A"
            ? "-"
            : d?.location?.region?.regionName,
        members: d?.members.length,
        districtName: d?.location?.district?.districtName || "-",
        chapterStatus: d.chapterStatus || "-",
        chapterLifecycleStatus: d.chapterLifecycleStatus || "-",
        _id: d._id,
      }));
  }, [
    chapterData,
    category,
    stateId,
    regionId,
    districtId,
    hasScopeRestriction,
    allowedStateIds,
    allowedDistrictIds,
    reactTableAs,
  ]);


  console.log("the Cahpters Data", baseRows) // while this has the chapter members count it doesnt have actual count of all the members. 

  const rows = useMemo(() => {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    if (!normalizedSearch) return baseRows;

    return baseRows.filter((row) =>
      row?.chapterName?.toLowerCase().includes(normalizedSearch),
    );
  }, [baseRows, searchTerm]);

  const totalChapterCount = baseRows.length;

  const vibrantChapterCount = baseRows.filter(
    (f) => f.chapterLifecycleStatus === "Vibrant",
  ).length;

  const prospectiveChapterCount = baseRows.filter(
    (f) => f.chapterLifecycleStatus === "Prospective",
  ).length;

  return (
    <>
      <Paper elevation={0} variant="pageBg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            pt: 1,
            pb: 1,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              alignContent: "center",
              marginBottom: "0px",
            }}
          >
            Chapters
          </Typography>
          {canCreateChapter && (
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: "#AEDAFF",
                color: "#202020",
                "&:hover": { transform: "translateY(-3px)", backgroundColor: "#AEDAFF", color: "#202020" },
              }}

              // color='AEDAFF'
              onClick={() => navigate(`/chapterDetailsPage`)}
            >
              <AddIcon />
              <Box
                component="span"
                sx={{
                  display: isSmallScreen ? "none" : "inline",
                  ml: 1,
                }}
              >
                New Chapter
              </Box>
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Grid
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 1, md: 1 }}
              alignItems="stretch"
              justifyContent="flex-start"
            >
              <Grid item xs={12} sm={6} md={6} lg={3} sx={{ display: "flex" }}>
                <CustomSelect
                  sx={{ width: "100%" }}
                  formControlSx={{ minWidth: 0 }}
                  name="category"
                  control={control}
                  label="Category (Vibrant / Prospective)"
                  options={[
                    { value: "All", label: "All" },
                    { value: "Prospective", label: "Prospective" },
                    { value: "Vibrant", label: "Vibrant" },
                  ]}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={3} sx={{ display: "flex" }}>
                <CustomSelect
                  sx={{ width: "100%" }}
                  formControlSx={{ minWidth: 0 }}
                  name="state"
                  control={control}
                  label="State"
                  options={[
                    ...(shouldShowAllStateOption
                      ? [{ value: "All", label: "All" }]
                      : []),
                    ...(filteredStates || []).map((state) => ({
                      value: state._id,
                      label: state.stateName,
                    })),
                  ]}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={3} sx={{ display: "flex" }}>
                <CustomSelect
                  sx={{ width: "100%" }}
                  formControlSx={{ minWidth: 0 }}
                  name="region"
                  control={control}
                  disabled={!stateId || stateId === "All"}
                  label="Region"
                  options={[
                    ...(shouldShowAllRegionOption
                      ? [{ value: "All", label: "All" }]
                      : []),
                    ...(filteredRegions || []).map((r) => ({
                      value: r._id,
                      label: r.regionName,
                    })),
                  ]}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={3} sx={{ display: "flex" }}>
                <CustomSelect
                  sx={{ width: "100%" }}
                  formControlSx={{ minWidth: 0 }}
                  name="district"
                  control={control}
                  disabled={!stateId || stateId === "All"}
                  label="District"
                  options={[
                    ...(shouldShowAllDistrictOption
                      ? [{ value: "All", label: "All" }]
                      : []),
                    ...(filteredDistricts || []).map((d) => ({
                      value: d._id,
                      label: d.districtName,
                    })),
                  ]}
                />
              </Grid>
            </Grid>

            <Grid
              container
              rowSpacing={1}
              columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              alignItems="stretch"
              justifyContent="flex-start"
              sx={{ mt: 1.5, mb: 2 }}
            >
              <Grid item xs={4} sm={4} md={4} sx={{ display: "flex" }}>
                <Box sx={{ ...statCard("#AEDAFF") }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: { xs: "0.8rem", sm: "1.1rem" }, textWrap: "wrap", lineHeight: 1.2, mb: 0 }}
                    color="#000000"
                  >
                    Total Chapters
                  </Typography>

                  <Typography
                    variant="h4"
                    sx={{ fontSize: { xs: "1.5rem", sm: "2.2rem" }, lineHeight: 1.1, mt: 0 }}
                    fontWeight={700}
                    color="#0b0b0b"
                  >
                    <CountUpAnimation
                      from={0}
                      to={totalChapterCount}
                      duration={1}
                    />
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4} sm={4} md={4} sx={{ display: "flex" }}>
                <Box sx={{ ...statCard("#AEDAFF") }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: { xs: "0.8rem", sm: "1.1rem" }, textWrap: "wrap", lineHeight: 1.2, mb: 0 }}
                    color="#000000"
                  >
                    Vibrant Chapters
                  </Typography>

                  <Typography
                    variant="h4"
                    sx={{ fontSize: { xs: "1.5rem", sm: "2.2rem" }, lineHeight: 1.1, mt: 0 }}
                    fontWeight={700}
                    color="#0b0b0b"
                  >
                    <CountUpAnimation
                      from={0}
                      to={vibrantChapterCount}
                      duration={1}
                    />
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4} sm={4} md={4} sx={{ display: "flex" }}>
                <Box sx={{ ...statCard("#AEDAFF") }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontSize: { xs: "0.8rem", sm: "1.1rem" }, textWrap: "wrap", lineHeight: 1.2, mb: 0 }}
                    color="#000000"
                  >
                    Prospective Chapters
                  </Typography>

                  <Typography
                    variant="h4"
                    sx={{ fontSize: { xs: "1.5rem", sm: "2.2rem" }, lineHeight: 1.1, mt: 0 }}
                    fontWeight={700}
                    color="#0b0b0b"
                  >
                    <CountUpAnimation
                      from={0}
                      to={prospectiveChapterCount}
                      duration={1}
                    />
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              width: "100%",
              maxWidth: 360,
              height: 200,
              mx: "auto",
              mt: 1.5,
            }}
          >
            <Doughnut
              data={{
                labels: ["Vibrant", "Prospective"],
                datasets: [
                  {
                    data: [vibrantChapterCount, prospectiveChapterCount],
                    backgroundColor: ["#69BBFF", "#FF8C6D"],
                    borderColor: ["#69BBFF", "#FF8C6D"],
                    borderWidth: 1,
                    hoverOffset: 10,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{ width: "100%", height: 2, bgcolor: "#C7C7C7", mb: 2, mt: 2 }}
        />
        {/* {environment && <Box>
          <Tabs value={tabValue} onChange={(event, newValue) => setTabValue(newValue)}>
            <Tab label="Chapters" onClick={() => setReactTableAs(true)}/>
            <Tab label="Requests" onClick={() => setReactTableAs(false)}/>
          </Tabs>
        </Box>} */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
            flexDirection: isSmallScreen ? "column" : "row",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap:0.5, width: "100%" }}>
            <Box sx={{ width:{xs: 250, sm:285, md:340} }}>
              <TextField
                fullWidth
                placeholder="Search by Chapter Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    bgcolor: "#fff",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#8A8A8A" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <MuiTooltip
              title="Download"
              placement="top"
              arrow
            >
              <span style={{alignContent:"center"}}>
                <Button
                  variant="contained"
                  //   color="primary"
                  size="small"
                  onClick={() => downloadChapterExcel(chapterData)}
                  aria-label="Download Excel"
                  sx={{
                    textTransform: "none",
                    borderRadius: 1,
                    backgroundColor: "#AEDAFF",
                    color: "#202020",
                    display:"flex",
                    flexDirection:"row",
                    gap:0.5,
                    "&:hover": { backgroundColor: "#AEDAFF", color: "#202020" },

                  }}
                >
                  <DownloadIcon />
                  {isSmallScreen ? null : "Download Excel" }
                </Button>
              </span>
            </MuiTooltip>
          </Box>
        </Box>
        <Box sx={{ width: "100%", mb: 4 }}>
          <ReactTable
            columns={columns}
            rows={rows}
            loading={loading}
            rowKey="chapterName"
            initialRowsPerPage={10}
            headerColor="linear-gradient(to right, #004ecb, #00c6ff)"
            headerTextColor="#fff"
          />

        </Box>
        {lastUpdated && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {lastUpdated.toLocaleDateString("en-GB")}
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default ChapterPage;
